/**
 * QuestionThread - One question about a property and the replies to it.
 *
 * Presentational, and written for either side of the conversation: `viewer`
 * says whose screen this is, so that side's messages read "You", the other
 * side's read `otherPartyLabel`, and the badge speaks to that viewer. The
 * caller fills `replySlot` (a follow-up box, or a line saying the other side
 * has been emailed).
 */

import type { ReactNode } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { Badge } from '@/common/components/badge'
import { Text } from '@/common/components/text'
import { isAwaitingAgentReply } from './isAwaitingAgentReply'
import * as styles from './QuestionThread.css'

export type QuestionThreadSender = 'investor' | 'agent'

export interface QuestionThreadEntry {
  id: string
  sender: QuestionThreadSender
  message: string
  /** ISO 8601 timestamp. */
  createdAt: string
}

export interface QuestionThreadProps {
  /** Whose screen the thread is on. */
  viewer: QuestionThreadSender
  /** What the other side's messages are labelled, for example "Listing agent". */
  otherPartyLabel: string
  /** Oldest first; the first entry is the question itself. */
  entries: readonly QuestionThreadEntry[]
  /** Rendered below the messages. */
  replySlot?: ReactNode
}

const BADGE_COPY: Record<QuestionThreadSender, { awaiting: string; answered: string }> = {
  investor: { awaiting: 'Awaiting reply', answered: 'Answered' },
  agent: { awaiting: 'Needs reply', answered: 'Replied' },
}

function formatRelativeTime(dateString: string): string | null {
  const date = new Date(dateString)
  // date-fns throws on an invalid date, which would unmount the whole page.
  if (Number.isNaN(date.getTime())) return null
  return formatDistanceToNow(date, { addSuffix: true })
}

export function QuestionThread({ viewer, otherPartyLabel, entries, replySlot }: QuestionThreadProps) {
  if (entries.length === 0) return null

  const awaiting = isAwaitingAgentReply(entries)
  const copy = BADGE_COPY[viewer]
  const askedAgo = formatRelativeTime(entries[0].createdAt)

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Text as="span" variant="bodyXs" color="muted">
          {askedAgo ? `Asked ${askedAgo}` : 'Asked'}
        </Text>
        <Badge color={awaiting ? 'caution' : 'success'} size="sm">
          {awaiting ? copy.awaiting : copy.answered}
        </Badge>
      </div>

      <ol className={styles.entries} aria-label="Messages">
        {entries.map((entry) => {
          const isOwn = entry.sender === viewer
          const sentAgo = formatRelativeTime(entry.createdAt)
          return (
            <li key={entry.id} className={styles.entry} data-side={isOwn ? 'own' : 'other'}>
              <div className={styles.entryMeta}>
                <Text as="span" variant="bodyXs" weight="bold">
                  {isOwn ? 'You' : otherPartyLabel}
                </Text>
                {sentAgo && (
                  <Text as="span" variant="bodyXs" color="muted">
                    {sentAgo}
                  </Text>
                )}
              </div>
              <Text variant="bodySm" className={styles.entryMessage}>
                {entry.message}
              </Text>
            </li>
          )
        })}
      </ol>

      {replySlot}
    </div>
  )
}
